import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitApplication, fileToBase64 } from '../../../services/jobApplicationService';

const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 2.5rem;
  transition: gap 0.2s ease;

  &:hover { gap: 0.7rem; }
`;

const JobHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 2rem;
  border-top: 4px solid ${props => props.theme.colors.accent};

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const JobTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const JobSubtitle = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 1.5rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-bottom: 1.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 160px;
`;

const MetaLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
`;

const MetaValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.4;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  background: ${props => props.highlight
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.07)'};
  color: ${props => props.highlight ? 'white' : props.theme.colors.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(16,29,64,0.08);
`;

const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;

  &:last-child { margin-bottom: 0; }
`;

const SubHeading = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.15rem;
  color: ${props => props.theme.colors.primary};
  margin: 1.5rem 0 0.6rem;
`;

const BulletList = styled.ul`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const NumberedList = styled.ol`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const StickyCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border-top: 4px solid ${props => props.theme.colors.primary};
  position: sticky;
  top: 140px;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.92rem;
  margin-bottom: 0.4rem;

  span.req { color: ${props => props.theme.colors.accent}; }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const FileUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.hasFile
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.2)'};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasFile
    ? 'rgba(16,29,64,0.04)'
    : 'transparent'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(16,29,64,0.03);
  }

  input { display: none; }
`;

const FileUploadText = styled.div`
  font-size: 0.88rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.selected ? '600' : '500'};
  opacity: ${props => props.selected ? 1 : 0.6};
`;

const FileUploadHint = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  margin-top: 0.35rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16,29,64,0.08);
  margin: 1.25rem 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.02em;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16,29,64,0.2);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  background: ${props => props.type === 'success'
    ? '#d4edda'
    : props.type === 'error'
    ? '#f8d7da'
    : '#d1ecf1'};
  color: ${props => props.type === 'success'
    ? '#155724'
    : props.type === 'error'
    ? '#721c24'
    : '#0c5460'};
  border: 1px solid ${props => props.type === 'success'
    ? '#c3e6cb'
    : props.type === 'error'
    ? '#f5c6cb'
    : '#bee5eb'};
`;

const SmallNote = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.6;
  line-height: 1.5;
  margin: 0.5rem 0 0;
  text-align: center;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 2rem 0;
`;

const SuccessIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const SuccessTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
  line-height: 1.6;
  margin: 0;
`;

const RuralEstateAssistant = () => {
  const formRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    portfolio: '',
    coverNote: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0] || null;
    setter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!cvFile) {
      setStatus({ type: 'error', message: 'Please attach your CV.' });
      return;
    }
    if (!coverFile) {
      setStatus({ type: 'error', message: 'Please attach your cover letter.' });
      return;
    }
    if (!form.coverNote.trim()) {
      setStatus({ type: 'error', message: 'Please include a cover note.' });
      return;
    }

    setSubmitting(true);
    try {
      const [cvData, coverData] = await Promise.all([
        fileToBase64(cvFile),
        fileToBase64(coverFile),
      ]);

      await submitApplication({
        roleId: 'rural-estate-assistant',
        roleTitle: 'Rural Estate Assistant — Land & Property',
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        portfolio: form.portfolio,
        coverNote: form.coverNote,
        cvData,
        cvFileName: cvFile.name,
        coverLetterData: coverData,
        coverLetterFileName: coverFile.name,
      });

      setSubmitted(true);
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        title="Rural Estate Assistant"
        subtitle="Land & Property Department · Belvoir Estate"
        image={DAY_TICKETS_BANNER_CDN}
      />

      <ContentContainer>
        <BackLink to="/about/careers">← Back to Careers</BackLink>

        <JobHeader>
          <JobTitle>Rural Estate Assistant</JobTitle>
          <JobSubtitle>Land &amp; Property — Belvoir Estate, Leicestershire</JobSubtitle>
          <MetaRow>
            <MetaItem>
              <MetaLabel>Salary</MetaLabel>
              <MetaValue>£26,000 – £30,000 per annum</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Reports To</MetaLabel>
              <MetaValue>Antony Haslam, Property Manager</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Contract</MetaLabel>
              <MetaValue>Full Time, Permanent</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Hours</MetaLabel>
              <MetaValue>40 hours per week</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Location</MetaLabel>
              <MetaValue>Belvoir Estate, Leicestershire</MetaValue>
            </MetaItem>
          </MetaRow>
          <TagRow>
            <Tag highlight>Now Hiring</Tag>
            <Tag>Property &amp; Land</Tag>
            <Tag>Administration</Tag>
            <Tag>Rural Estate</Tag>
            <Tag>Full Time</Tag>
          </TagRow>
        </JobHeader>

        <Grid>
          <div>
            <Section>
              <SectionTitle>About Belvoir Estate</SectionTitle>
              <BodyText>
                Belvoir Estate is a large, mixed-use rural estate in Leicestershire comprising approximately
                500 tenancies and a multi-million pound rent roll. The estate's portfolio spans residential,
                commercial, agricultural, renewables, hospitality, leisure, minerals, and events, held across
                multiple trusts, companies and limited liability partnerships.
              </BodyText>
              <BodyText>
                The Land &amp; Property department is central to the estate's operations and has plans for
                growth across a number of key income-generating projects.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>The Role</SectionTitle>
              <BodyText>
                This is a newly created post arising from a strategic restructure of the Land &amp; Property
                department. As the estate grows and takes on increasingly complex and high-value projects —
                including renewables, leisure, planning and development, and commercial asset management —
                the team requires dedicated administrative and operational support to free up senior capacity
                for project delivery and asset management.
              </BodyText>
              <BodyText>
                The Rural Estate Assistant will sit at the heart of day-to-day operations, providing broad
                administrative and operational support to the Property Manager and Assistant Land &amp; Property
                Manager, whilst also providing cover and resilience across the wider team. This is an excellent
                opportunity for a motivated and organised individual looking to work in rural estate or property
                management.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>Key Responsibilities</SectionTitle>

              <SubHeading>1. Administrative Support</SubHeading>
              <BulletList>
                <li>Provide comprehensive day-to-day administrative support to the Property Manager and Assistant Land &amp; Property Manager, reducing routine tasks falling to senior team members.</li>
                <li>Manage incoming post, scanning, filing, and document management in line with department procedures.</li>
                <li>Maintain and manage diaries for the Property Manager, including scheduling meetings, viewings, and site visits.</li>
                <li>Draft and process standard correspondence, letters, and emails on behalf of the team.</li>
                <li>Process and manage paddock licences and small land agreements from instruction through to execution, maintaining accurate records throughout.</li>
                <li>Maintain accurate and up-to-date data entry across the property management system, including tenancy records, rent schedules, and compliance data.</li>
              </BulletList>

              <SubHeading>2. Compliance &amp; Performance Reporting</SubHeading>
              <BulletList>
                <li>Produce regular compliance and performance reports across the let estate, drawing data from the property management system and presenting findings clearly to the Property Manager.</li>
                <li>Support the transition from Landmark to the estate's new cloud-based property management system, assisting with data migration, system population, and ongoing data integrity.</li>
                <li>Assist in producing management information and dashboards for internal reporting purposes.</li>
              </BulletList>

              <SubHeading>3. Debt Management</SubHeading>
              <BulletList>
                <li>Take ownership of first-stage debt management correspondence across the estate.</li>
                <li>Issue arrears letters, email chasers, and formal notices at appropriate stages of the debt management process in accordance with agreed procedures and legal requirements.</li>
                <li>Maintain an up-to-date arrears log, tracking progress on each case and escalating to the relevant team member where legal action or landlord intervention is required.</li>
                <li>Liaise with tenants professionally and efficiently to resolve debt matters at the earliest opportunity, maintaining positive relationships wherever possible.</li>
              </BulletList>

              <SubHeading>4. Team Cover &amp; Operational Resilience</SubHeading>
              <BulletList>
                <li>Provide effective cover for the Property Administrator and the Maintenance &amp; Compliance Coordinator during periods of absence, leave, or high workload.</li>
                <li>Answer incoming telephone calls and respond to tenant enquiries, triaging issues appropriately and resolving straightforward queries independently. This will include some 24h and weekend emergency phone cover.</li>
                <li>Assist with the management of reactive maintenance requests, including logging jobs, instructing approved contractors, and following up to ensure timely completion.</li>
                <li>Conduct accompanied property viewings and assist prospective tenants through the sign-up process, including referencing, tenancy documentation, and key handover.</li>
                <li>Process end-of-tenancy administration including check-out coordination, deposit returns, and dilapidations correspondence as directed.</li>
              </BulletList>

              <SubHeading>5. General Estate Support</SubHeading>
              <BulletList>
                <li>Support the department with ad hoc tasks and project-based work as directed by the Property Manager.</li>
                <li>Assist with preparation for estate meetings, compiling papers, agendas, and action logs as required.</li>
                <li>Maintain awareness of the broader estate portfolio and key projects to provide informed and effective support across the team.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>Person Specification</SectionTitle>

              <SubHeading>Essential</SubHeading>
              <BulletList>
                <li>Strong administrative and organisational skills with high attention to detail.</li>
                <li>Excellent written and verbal communication skills, with the ability to draft clear and professional correspondence.</li>
                <li>Confident and professional telephone manner, comfortable dealing with a wide range of people.</li>
                <li>Proficient in Microsoft Office (Word, Excel, Outlook) and comfortable working with digital systems and databases.</li>
                <li>Ability to manage a varied and fast-moving workload, prioritise effectively, and meet deadlines.</li>
                <li>A proactive and self-motivated approach — able to work independently and as part of a small, close-knit team.</li>
                <li>Discretion and professionalism in handling confidential tenancy and financial information.</li>
              </BulletList>

              <SubHeading>Desirable</SubHeading>
              <BulletList>
                <li>Previous experience in a property, estate management, or land agency environment.</li>
                <li>Familiarity with property management software such as Landmark, or similar platforms.</li>
                <li>An interest in rural estates, agriculture, or the countryside more broadly.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>Team Structure</SectionTitle>
              <BodyText>
                The Land &amp; Property department currently comprises four members of staff:
              </BodyText>
              <BulletList>
                <li><strong>Property Manager</strong> — overall department lead, complex estate issues, key projects, and strategic development.</li>
                <li><strong>Assistant Land &amp; Property Manager</strong> — day-to-day asset management, parkland events, small land agreements, commercial and agricultural debt, and estate mapping.</li>
                <li><strong>Property Administrator</strong> — residential re-lettings, rent reviews, residential debtors, and account reconciliation.</li>
                <li><strong>Maintenance &amp; Compliance Coordinator</strong> — reactive and planned maintenance, contractor relationships, compliance obligations, and end-of-tenancy management.</li>
              </BulletList>
              <BodyText>
                The Rural Estate Assistant will report directly to the Property Manager and will work closely with
                all members of the team. The appointment forms part of a broader restructure designed to increase
                capacity, improve performance oversight, and enable the Property Manager to take a more strategic
                and project-focused role within the estate.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>What We Offer</SectionTitle>
              <BulletList>
                <li>A varied and rewarding role within a prestigious rural estate.</li>
                <li>Competitive salary of £26,000 – £30,000 depending on experience.</li>
                <li>Exposure to a diverse property portfolio across multiple asset classes.</li>
                <li>A collaborative and supportive team environment.</li>
              </BulletList>
            </Section>
          </div>

          <StickyCard ref={formRef}>
            {submitted ? (
              <SuccessWrap>
                <SuccessIcon>✓</SuccessIcon>
                <SuccessTitle>Application Received</SuccessTitle>
                <SuccessText>
                  Thank you for your application. We'll be in touch if your experience matches what we're looking for.
                </SuccessText>
              </SuccessWrap>
            ) : (
              <>
                <CardTitle>Apply for This Role</CardTitle>
                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>First Name <span className="req">*</span></Label>
                    <Input
                      type="text"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="Jane"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Last Name <span className="req">*</span></Label>
                    <Input
                      type="text"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Email Address <span className="req">*</span></Label>
                    <Input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@example.com"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+44 7700 900000"
                    />
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>CV <span className="req">*</span></Label>
                    <FileUploadArea hasFile={!!cvFile}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange(setCvFile)}
                      />
                      <FileUploadText selected={!!cvFile}>
                        {cvFile ? cvFile.name : 'Choose file — CV'}
                      </FileUploadText>
                      {!cvFile && (
                        <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                      )}
                    </FileUploadArea>
                  </FormGroup>

                  <FormGroup>
                    <Label>Cover Letter <span className="req">*</span></Label>
                    <FileUploadArea hasFile={!!coverFile}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange(setCoverFile)}
                      />
                      <FileUploadText selected={!!coverFile}>
                        {coverFile ? coverFile.name : 'Choose file — Cover Letter'}
                      </FileUploadText>
                      {!coverFile && (
                        <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                      )}
                    </FileUploadArea>
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>Cover Note <span className="req">*</span></Label>
                    <TextArea
                      name="coverNote"
                      value={form.coverNote}
                      onChange={handleChange}
                      placeholder="Tell us what draws you to rural estate management, any relevant experience you have, and why you'd like to work at Belvoir."
                      rows={5}
                      required
                    />
                    <SmallNote>Applications without a cover note will not be considered.</SmallNote>
                  </FormGroup>

                  {status && (
                    <StatusMessage type={status.type}>{status.message}</StatusMessage>
                  )}

                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </SubmitButton>

                  <SmallNote style={{ marginTop: '0.85rem' }}>
                    Your information is kept confidential and used only for this recruitment process.
                  </SmallNote>
                </form>
              </>
            )}
          </StickyCard>
        </Grid>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default RuralEstateAssistant;
